

















































import Component, { mixins } from "vue-class-component";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PersonalInformation } from "@scrile/api-provider/dist/api/PersonalInformationProvider";
import { UserVerification, UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { YotiIdVerification } from "@scrile/api-provider/dist/api/YotiVerificationProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithUserVerification from "@/mixins/WithUserVerification";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSpinner from "@/components/BaseSpinner";
import CardInfo from "@/components/CardInfo/CardInfo.vue";
import ConfirmUpdateInfo from "@/components/ConfirmUpdateInfo";
import PageTitle from "@/components/PageTitle";
import getComponent from "../integrations";

interface Info {
  title: string;
  items: InfoItem[];
}

interface InfoItem {
  name: string;
  value: string | null;
  icon?: string;
}

@Component({
  name: "PayeeInfoView",
  components: { ConfirmUpdateInfo, CardInfo, PageTitle, BaseIcon, BaseSpinner, BaseIconButton },
})
export default class PayeeInfoView extends mixins(WithAuthUser, WithUserVerification) {
  public loading: boolean = false;

  public personalInformation: PersonalInformation | null = null;

  public userVerificationStatus: UserVerification | null = null;

  public payeeInformation: PayeeInformation | null = null;

  public yotiIdVerification: YotiIdVerification | null = null;

  public isShowUpdateInfo: boolean = false;

  public get showWaitingMessage() {
    return this.authUserVerificationStatus === UserVerificationStatus.WAITING_FOR_APPROVAL;
  }

  public get allowEditPayeeInfo() {
    return this.authUserVerificationStatus !== UserVerificationStatus.UNVERIFIED;
  }

  get info(): Info[] {
    if (!this.personalInformation || !this.userVerificationStatus || !this.payeeInformation) return [];
    const integration = getComponent(this.payeeInformation.payoutSystem);
    const listField = integration.listField;

    return [
      {
        title: $t("Payee information"),
        items: [
          {
            name: $t("First name"),
            value: this.personalInformation.firstName,
          },
          {
            name: $t("Last name"),
            value: this.personalInformation.lastName,
          },
          {
            name: $t("Phone number"),
            value: this.personalInformation.phone,
          },
        ],
      },
      ...(this.yotiIdVerification?.verified
        ? []
        : [
            {
              title: $t("Age"),
              items: [
                {
                  name: $t("Photo or scan of your ID"),
                  value: this.userVerificationStatus.idPhoto ? $t("Attached") : $t("Not attached"),
                  icon: this.userVerificationStatus.idPhoto ? "sci-lock" : undefined,
                },
                {
                  name: $t("Your photo with ID"),
                  value: this.userVerificationStatus.photoWithId ? $t("Attached") : $t("Not attached"),
                  icon: this.userVerificationStatus.idPhoto ? "sci-lock" : undefined,
                },
              ],
            },
          ]),
      {
        title: $t("Payout system"),
        items: [
          {
            name: $t("Payout system"),
            value: listField?.text ?? "",
          },
          {
            name: $t("Currency"),
            value: this.payeeInformation.currency,
          },
        ],
      },
    ];
  }

  public async getUserVerification() {
    let data = await this.userVerificationSearch({ userIds: [this.authUserStrict.id] });
    this.userVerificationStatus = data.result.find(i => i.userId === this.authUserStrict.id) || null;
  }

  public async getData() {
    try {
      this.loading = true;
      await Promise.all([
        providers.PersonalInformationProvider.find({ id: this.authUserStrict.id }).then(
          r => (this.personalInformation = r)
        ),
        this.getUserVerification(),
        providers.YotiVerificationProvider.get().then(r => (this.yotiIdVerification = r)),
        providers.PayeeInformationProvider.find({ id: this.authUserStrict.id }).then(r => {
          this.payeeInformation = r;
          this.isShowUpdateInfo = r.payoutSystem !== "masspay";
        }),
      ]);
    } finally {
      this.loading = false;
    }
  }

  public created() {
    this.getData();
  }
}
